/**
 * @fileoverview Servizio per autenticazione (Firebase Auth).
 * Usa le costanti di dataModel e db/auth da firebase.js.
 */
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  reload,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { COLLECTIONS, USERS_FIELDS } from '../domain/dataModel';

/**
 * Login con email e password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import('firebase/auth').User>}
 */
export async function loginWithEmail(email, password) {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Login con Google (popup).
 * @returns {Promise<import('firebase/auth').User>}
 */
export async function loginWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const { user } = await signInWithPopup(auth, provider);
    const userRef = doc(db, COLLECTIONS.USERS, user.uid);
    await setDoc(
      userRef,
      {
        [USERS_FIELDS.EMAIL]: user.email,
        [USERS_FIELDS.DISPLAY_NAME]: user.displayName || null,
        [USERS_FIELDS.CREATED_AT]: serverTimestamp(),
        [USERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      },
      { merge: true }
    );
    return user;
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Registrazione con email, password e nome.
 * Crea utente Auth e documento in users; invia email di verifica.
 * @param {string} email
 * @param {string} password
 * @param {string} [displayName]
 * @returns {Promise<void>}
 */
export async function registerWithEmail(email, password, displayName = null) {
  try {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await sendEmailVerification(user);
    } catch (e) {
      console.error('Invio email di verifica fallito:', e);
    }
    const userRef = doc(db, COLLECTIONS.USERS, user.uid);
    await setDoc(userRef, {
      [USERS_FIELDS.EMAIL]: email,
      ...(displayName && { [USERS_FIELDS.DISPLAY_NAME]: displayName }),
      [USERS_FIELDS.CREATED_AT]: serverTimestamp(),
      [USERS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Invia email di verifica all'utente corrente.
 * @param {import('firebase/auth').User} user
 * @returns {Promise<void>}
 */
export async function sendVerificationEmail(user) {
  try {
    await sendEmailVerification(user);
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Logout dall'app.
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await signOut(auth);
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Ricarica i dati dell'utente da Firebase Auth (es. dopo verifica email).
 * @param {import('firebase/auth').User} user
 * @returns {Promise<void>}
 */
export async function reloadUser(user) {
  try {
    await reload(user);
  } catch (err) {
    throw new Error(getAuthErrorMessage(err));
  }
}

/**
 * Restituisce un messaggio in italiano per gli errori Firebase Auth.
 * @param {Error} err
 * @returns {string}
 */
export function getAuthErrorMessage(err) {
  if (!err?.code) return err?.message || 'Si è verificato un errore.';
  const messages = {
    'auth/email-already-in-use': 'Questa email è già registrata.',
    'auth/invalid-email': 'Email non valida.',
    'auth/operation-not-allowed': 'Operazione non consentita.',
    'auth/weak-password': 'La password deve essere di almeno 6 caratteri.',
    'auth/user-disabled': 'Questo account è stato disabilitato.',
    'auth/user-not-found': 'Nessun account associato a questa email.',
    'auth/wrong-password': 'Password errata.',
    'auth/invalid-credential': 'Email o password non corretti.',
    'auth/invalid-login-credentials': 'Email o password non corretti.',
    'auth/too-many-requests': 'Troppi tentativi. Riprova più tardi.',
    'auth/popup-closed-by-user': 'Accesso con Google annullato.',
    'auth/popup-blocked': 'Pop-up bloccato. Consenti i pop-up per questo sito.',
    'auth/cancelled-popup-request': 'Accesso annullato.',
    'auth/account-exists-with-different-credential':
      'Esiste già un account con questa email. Usa un altro metodo di accesso.',
  };
  return messages[err.code] || err.message || 'Si è verificato un errore.';
}
